import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography
} from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import {
  InboundTripSendToBrokerTransporterDto,
  InboundTripSendToBrokerTransporterSummaryDto,
  inboundTripSendToBrokerTransporterValidationSchema
} from 'dtos'
import { Formik } from 'formik'

interface SendToBrokerTransporterDialogProps {
  initialValues: InboundTripSendToBrokerTransporterDto
  loading: boolean
  onClose: () => void
  onSave: (values: InboundTripSendToBrokerTransporterDto) => void
  open: boolean
  summary: InboundTripSendToBrokerTransporterSummaryDto
}

export default function SendToBrokerTransporterDialog({
  initialValues,
  loading,
  onClose,
  onSave,
  open,
  summary
}: SendToBrokerTransporterDialogProps) {
  return (
    <Dialog fullWidth maxWidth='md' open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          values.sentToBrokerDateTime = new Date().toISOString()
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur
        validateOnChange
        validationSchema={inboundTripSendToBrokerTransporterValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogTitle>Send to Broker / Transporter</DialogTitle>

              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <LabelField label='Import Branch' value={summary.importBranch} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Port of Lading' value={summary.portOfLading} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Port of Entry' value={summary.portOfEntry} />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant='body2'>PAPS #s and Vehicle Counts</Typography>

                    {summary.paps.map(papsDetails => (
                      <Box key={papsDetails.papsNumber} sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                        <Typography color='primary' variant='body1'>
                          {papsDetails.papsNumber || '-'}
                        </Typography>

                        <Typography variant='body1'>-</Typography>

                        <Typography color='primary' variant='body1'>
                          {papsDetails.numberOfVehicles} {papsDetails.numberOfVehicles > 1 ? 'Vehicles' : 'Vehicle'}
                        </Typography>
                      </Box>
                    ))}

                    {summary.paps.length === 0 && <Typography variant='body1'>No PAPS are associated with this inbound trip.</Typography>}
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isSubmitting || loading}
                      error={Boolean(touched.specialNotes && errors.specialNotes)}
                      fullWidth
                      helperText={touched.specialNotes && errors.specialNotes}
                      label='Special Notes'
                      maxRows={4}
                      minRows={4}
                      multiline
                      name='specialNotes'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      size='small'
                      value={values.specialNotes || ''}
                      variant='outlined'
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || The Special Notes field contents will be added to the body of the “Send to Broker” email. This email
                      will automatically display the Inbound Trip#, the Inbound Transporter Company name, the PAPS#s of all PAPS
                      in the Inbound Trip, and the email address of the user who pressed “Send” as the primary contact for any
                      questions about the Inbound Trip. ||
                    </ExplanationAccordion>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button disabled={loading || isSubmitting} variant='text' color='inherit' onClick={onClose}>
                  CANCEL
                </Button>

                <LoadingButton loading={loading || isSubmitting} type='submit' variant='contained'>
                  SEND
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
